// store/slices/language-slice.ts
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

// Language configuration (single source of truth)
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  getLanguageFromCountry,
  getLanguageConfig,
} from "config/language-config";

// Common utilities
import getGeolocation from "common/get-geolocation";
import getBrowserLanguage from "common/get-browser-language";
import getInitialLanguage from "common/get-initial-language";

// ============================================================================
// Types
// ============================================================================

type DetectionSource = "localStorage" | "browser" | "geo" | "default";

interface LanguageState {
  current: string;
  available: string[];
  isLoading: boolean;
  detectionSource: DetectionSource | null;
  geoCountry: string | null;
  browserLang: string | null;
}

interface DetectionResult {
  language: string;
  source: DetectionSource;
  browserLang: string | null;
  geoCountry: string | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize a browser language code to a supported language.
 * Handles full codes (en-US), base codes (en), and regional variants.
 */
function normalizeBrowserLanguage(
  langCode: string,
  countryCode?: string | null
): string | null {
  // Direct match with supported languages
  if (getLanguageConfig(langCode)) {
    return langCode;
  }

  // Extract base language (e.g., 'en' from 'en-US')
  const baseLang = langCode.split("-")[0].toLowerCase();

  // Special handling for Chinese variants
  if (baseLang === "zh") {
    const subTag = langCode.split("-")[1]?.toUpperCase();

    // Check subtag first (zh-TW, zh-HK, zh-CN)
    if (subTag === "TW" || subTag === "HANT") return DEFAULT_LANGUAGE;
    if (subTag === "HK") return DEFAULT_LANGUAGE;
    if (subTag === "CN" || subTag === "HANS") return DEFAULT_LANGUAGE;

    // Fall back to country code
    if (countryCode === "TW") return DEFAULT_LANGUAGE;
    if (countryCode === "HK") return DEFAULT_LANGUAGE;
    if (countryCode === "CN") return DEFAULT_LANGUAGE;

    // Default to Simplified Chinese
    return DEFAULT_LANGUAGE;
  }

  // Special handling for English variants
  if (baseLang === "en") {
    const subTag = langCode.split("-")[1]?.toUpperCase();

    if (subTag === "US") return DEFAULT_LANGUAGE;
    if (subTag === "SG") return DEFAULT_LANGUAGE;
    if (countryCode === "US") return DEFAULT_LANGUAGE;
    if (countryCode === "SG") return DEFAULT_LANGUAGE;

    return DEFAULT_LANGUAGE;
  }

  // Check if base language is directly supported
  if (getLanguageConfig(baseLang)) {
    return baseLang;
  }

  return DEFAULT_LANGUAGE;
}

// ============================================================================
// Async Thunk
// ============================================================================

/**
 * Detect the user's preferred language using multiple sources.
 * Priority: localStorage > geolocation > browser language > default
 */
export const detectLanguage = createAsyncThunk<
  DetectionResult,
  void,
  { rejectValue: string }
>("language/detectLanguage", async (_, { rejectWithValue }) => {
  try {
    const browserLangRaw = getBrowserLanguage();

    // Priority 1: Check localStorage
    const storedLang = localStorage.getItem("lang");
    if (storedLang && getLanguageConfig(storedLang)) {
      return {
        language: storedLang,
        source: "localStorage",
        browserLang: browserLangRaw,
        geoCountry: null,
      };
    }

    // Priority 2: Get geolocation
    const geoData = await getGeolocation();
    const geoCountryCode = geoData?.countryCode ?? null;
    const geoLang = geoCountryCode
      ? getLanguageFromCountry(geoCountryCode)
      : null;

    if (geoLang) {
      localStorage.setItem("lang", geoLang);
      return {
        language: geoLang,
        source: "geo",
        browserLang: browserLangRaw,
        geoCountry: geoData?.country ?? null,
      };
    }

    // Priority 3: Browser language
    const browserLang = browserLangRaw
      ? normalizeBrowserLanguage(browserLangRaw, geoCountryCode)
      : null;

    if (browserLang) {
      localStorage.setItem("lang", browserLang);
      return {
        language: browserLang,
        source: "browser",
        browserLang: browserLangRaw,
        geoCountry: geoData?.country ?? null,
      };
    }

    // Priority 4: Default fallback
    localStorage.setItem("lang", DEFAULT_LANGUAGE);
    return {
      language: DEFAULT_LANGUAGE,
      source: "default",
      browserLang: browserLangRaw,
      geoCountry: geoData?.country ?? null,
    };
  } catch (error) {
    console.error("Language detection failed:", error);
    return rejectWithValue("Failed to detect language");
  }
});

// ============================================================================
// Slice
// ============================================================================

const initialState: LanguageState = {
  current: getInitialLanguage(),
  available: AVAILABLE_LANGUAGES,
  isLoading: false,
  detectionSource: null,
  geoCountry: null,
  browserLang: null,
};

const languageSlice = createSlice({
  name: "language",
  initialState,
  reducers: {
    setLanguage: (state, action: PayloadAction<string>) => {
      const lang = action.payload;
      // Validate the language exists in config
      if (getLanguageConfig(lang)) {
        state.current = lang;
        state.detectionSource = "localStorage";
        localStorage.setItem("lang", lang);
      } else {
        console.warn(`Invalid language code: ${lang}, falling back to default`);
        state.current = DEFAULT_LANGUAGE;
        localStorage.setItem("lang", DEFAULT_LANGUAGE);
      }
    },
    clearLanguagePreference: (state) => {
      localStorage.removeItem("lang");
      state.current = DEFAULT_LANGUAGE;
      state.detectionSource = "default";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(detectLanguage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(detectLanguage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload.language;
        state.detectionSource = action.payload.source;
        state.browserLang = action.payload.browserLang;
        state.geoCountry = action.payload.geoCountry;
      })
      .addCase(detectLanguage.rejected, (state, action) => {
        state.isLoading = false;
        state.current = DEFAULT_LANGUAGE;
        state.detectionSource = "default";
        console.error("Language detection failed:", action.payload);
      });
  },
});

export const { setLanguage, clearLanguagePreference } = languageSlice.actions;
export default languageSlice.reducer;
